import React, { useState } from 'react';

import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { useGetApiV5FirmsContactsId } from '@liscio/api';
import {
  Box,
  Typography,
  Stack,
  IconButton,
  Link,
  styled,
  Chip,
  Avatar,
  Skeleton,
  useTheme,
  Icon,
} from '@liscio/ui';

import { formatSMSPhonePretty } from '../../modules/sms/helpers/formatSMSPhone';
import { useFirmViewMode } from 'custom-hooks/useFirmViewMode';

export interface ContactCardProps {
  contactId: number | string;
  /**
   * If true, the card takes full width (used in mobile drawer)
   */
  fullWidth?: boolean;
  /**
   * Optional close handler (shows close button when provided)
   */
  onClose?: () => void;
  /** Shows navigation links that leave the current flow. */
  showNavigationLinks?: boolean;
}

const CardContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'fullWidth',
})<{ fullWidth?: boolean }>(({ theme, fullWidth }) => ({
  width: fullWidth ? '100%' : 500,
  maxWidth: fullWidth ? '100%' : '90vw',
  backgroundColor: theme.palette.background.paper,
  borderRadius: fullWidth ? 0 : theme.shape.borderRadius,
  overflow: 'hidden',
  boxShadow: fullWidth ? 'none' : theme.shadows[8],
}));

const HeaderSection = styled(Box)({
  backgroundColor: '#434566',
  padding: '16px',
  position: 'relative',
});

const HeaderContent = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
});

const ContactAvatar = styled(Avatar)(({ theme }) => ({
  width: 56,
  height: 56,
  fontSize: '1.25rem',
  border: `2px solid ${theme.palette.common.white}`,
  backgroundColor: theme.palette.primary.main,
}));

const ContactName = styled(Typography)({
  color: '#fff',
  fontWeight: 600,
  fontSize: '1rem',
  lineHeight: 1.3,
});

const StatusChip = styled(Chip)<{ statusColor?: string }>(
  ({ statusColor }) => ({
    height: 24,
    fontSize: '0.75rem',
    fontWeight: 600,
    backgroundColor: statusColor || '#4caf50',
    color: '#fff',
    borderRadius: 12,
    width: 'fit-content',
    '& .MuiChip-label': {
      padding: '0 10px',
    },
  })
);

const OpenContactLink = styled(Link)({
  color: '#fff',
  fontSize: '0.8125rem',
  fontWeight: 600,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  cursor: 'pointer',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  '&:hover': {
    textDecoration: 'underline',
    color: '#fff',
  },
});

const BodySection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.6875rem',
  fontWeight: 500,
  color: theme.palette.text.secondary,
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  marginBottom: theme.spacing(0.5),
}));

const FieldValue = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
}));

const ValueText = styled(Typography)(({ theme }) => ({
  fontSize: '0.9375rem',
  fontWeight: 500,
  color: theme.palette.primary.main,
}));

const CopyButton = styled(IconButton)(({ theme }) => ({
  padding: 4,
  color: theme.palette.text.secondary,
  '&:hover': {
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
  },
}));

const AccountItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1, 0),
}));

const AccountInfo = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flex: 1,
  minWidth: 0,
});

const AccountAvatar = styled(Avatar)(({ theme }) => ({
  width: 32,
  height: 32,
  fontSize: '0.75rem',
  backgroundColor: theme.palette.grey[300],
  color: theme.palette.text.primary,
}));

const AccountName = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 500,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: theme.palette.text.primary,
}));

const OpenAccountLink = styled(Link)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: '0.8125rem',
  fontWeight: 500,
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  flexShrink: 0,
  cursor: 'pointer',
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

const getInitials = (firstName?: string, lastName?: string): string => {
  const first = firstName?.charAt(0) || '';
  const last = lastName?.charAt(0) || '';
  return (first + last).toUpperCase();
};

export const ContactCard: React.FC<ContactCardProps> = ({
  contactId,
  fullWidth = false,
  onClose,
  showNavigationLinks = true,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { isMobile } = useFirmViewMode();
  const { data, isLoading } = useGetApiV5FirmsContactsId(String(contactId));
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const contact = data?.data;
  const accounts = contact?.accounts || [];
  const phone = contact?.phone_numbers[0]?.phone_number;
  const email = contact?.primary_email;

  const fullName = contact
    ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim()
    : '';

  // Get status color from theme
  const getStatusColor = (status?: string): string => {
    switch (status?.toLowerCase()) {
      case 'active':
        return theme.palette.success.main;
      case 'invited':
        return theme.palette.info?.main || theme.palette.primary.main;
      case 'new':
        return theme.palette.warning.main;
      case 'inactive':
      case 'expired':
        return theme.palette.grey[500];
      default:
        return theme.palette.success.main;
    }
  };

  const handleCopy = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      toast.success(`${fieldName} copied to clipboard`);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleOpenContact = () => {
    if (contact?.id) {
      if (isMobile) {
        navigate(`/contacts/details/${contact.id}/overview`);
      } else {
        window.location.assign(`/contactdetails/${contact.id}/overview`);
      }
    }
  };

  const handleOpenAccount = (accountId: number) => {
    if (isMobile) {
      navigate(`/accounts/details/${accountId}/overview`);
    } else {
      window.location.assign(`/accountdetails/${accountId}/activity`);
    }
  };

  if (isLoading) {
    return (
      <CardContainer fullWidth={fullWidth}>
        <HeaderSection>
          <HeaderContent>
            <Skeleton variant="circular" width={56} height={56} />
            <Stack flex={1} gap={1}>
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="rounded" width={60} height={24} />
            </Stack>
          </HeaderContent>
        </HeaderSection>
        <BodySection>
          <Skeleton variant="text" width="30%" height={16} />
          <Skeleton variant="text" width="80%" height={24} />
          <Skeleton variant="text" width="30%" height={16} sx={{ mt: 2 }} />
          <Skeleton variant="text" width="60%" height={24} />
        </BodySection>
      </CardContainer>
    );
  }

  if (!contact) {
    return (
      <CardContainer fullWidth={fullWidth}>
        <BodySection>
          <Typography color="text.secondary">
            Contact information not available
          </Typography>
        </BodySection>
      </CardContainer>
    );
  }

  return (
    <CardContainer fullWidth={fullWidth}>
      <HeaderSection>
        {/* Close button for mobile drawer */}
        {onClose && (
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: '#fff',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)',
              },
            }}
            aria-label="Close"
          >
            <Icon icon="close" sx={{ fontSize: 20 }} />
          </IconButton>
        )}
        <HeaderContent>
          <ContactAvatar>
            {getInitials(contact?.first_name || undefined, contact?.last_name || undefined)}
          </ContactAvatar>
          <Stack gap={0.5} flex={1} pt={0.5} minWidth={0} pr={onClose ? 4 : 0}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
              gap={1}
            >
              <ContactName
                sx={{
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {fullName}
              </ContactName>
              {showNavigationLinks && (
                <OpenContactLink onClick={handleOpenContact}>
                  Open Contact
                  <Icon icon="arrowRight" sx={{ fontSize: 14, color: '#fff' }} />
                </OpenContactLink>
              )}
            </Box>
            <StatusChip
              label={contact.status || 'Active'}
              statusColor={getStatusColor(contact.status)}
              size="small"
            />
          </Stack>
        </HeaderContent>
      </HeaderSection>

      <BodySection>
        {/* Email Section */}
        <FieldLabel>EMAIL:</FieldLabel>
        <FieldValue>
          <ValueText>{email || '-'}</ValueText>
          {email && (
            <CopyButton
              size="small"
              onClick={() => handleCopy(email, 'Email')}
              aria-label="Copy email"
            >
              <Icon
                icon={copiedField === 'Email' ? 'check' : 'copy'}
                sx={{ fontSize: 18 }}
              />
            </CopyButton>
          )}
        </FieldValue>

        {/* Phone Section */}
        <FieldLabel>MOBILE #:</FieldLabel>
        <FieldValue>
          <ValueText>{phone ? formatSMSPhonePretty(phone) : '-'}</ValueText>
          {phone && (
            <CopyButton
              size="small"
              onClick={() => handleCopy(phone, 'Phone')}
              aria-label="Copy phone number"
            >
              <Icon
                icon={copiedField === 'Phone' ? 'check' : 'copy'}
                sx={{ fontSize: 18 }}
              />
            </CopyButton>
          )}
        </FieldValue>

        {/* Associated Accounts Section */}
        {accounts.length > 0 && (
          <>
            <FieldLabel>ASSOCIATED ACCOUNTS:</FieldLabel>
            <Stack>
              {accounts.slice(0, 3).map((account) => {
                // Split account name to get initials
                const nameParts = account.name.split(/\s+/);
                const accountInitials = getInitials(nameParts[0], nameParts[1]);

                return (
                  <AccountItem key={account.uuid}>
                    <AccountInfo>
                      <AccountAvatar>{accountInitials}</AccountAvatar>
                      <AccountName>{account.name}</AccountName>
                    </AccountInfo>
                    {showNavigationLinks && (
                      <OpenAccountLink
                        onClick={() => handleOpenAccount(account.id)}
                      >
                        Open Account
                        <Icon icon="arrowRight" sx={{ fontSize: 14 }} />
                      </OpenAccountLink>
                    )}
                  </AccountItem>
                );
              })}
            </Stack>
          </>
        )}
      </BodySection>
    </CardContainer>
  );
};

export default ContactCard;
